/* eslint-disable no-await-in-loop */
/* eslint-disable no-empty-function */
/* eslint-disable no-useless-constructor */
export type HTTPQuery = {
  [key: string]:
    | string
    | number
    | null
    | boolean
    | Array<string | number | null | boolean>
    | Set<string | number | null | boolean>
    | HTTPQuery;
};
export type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OPTIONS' | 'HEAD';
export type HTTPHeaders = { [key: string]: string };
export type Json = any;
export type HTTPBody = Json | FormData | URLSearchParams;

export function querystring(params: HTTPQuery, prefix: string = ''): string {
  return (
    Object.keys(params)
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      .map((key) => querystringSingleKey(key, params[key], prefix))
      .filter((part) => part.length > 0)
      .join('&')
  );
}

function querystringSingleKey(
  key: string,
  value:
    | string
    | number
    | null
    | undefined
    | boolean
    | Array<string | number | null | boolean>
    | Set<string | number | null | boolean>
    | HTTPQuery,
  keyPrefix: string = '',
): string {
  const fullKey = keyPrefix + (keyPrefix.length ? `[${key}]` : key);
  if (value instanceof Array) {
    const multiValue = value
      .map((singleValue) => encodeURIComponent(String(singleValue)))
      .join(`&${encodeURIComponent(fullKey)}=`);
    return `${encodeURIComponent(fullKey)}=${multiValue}`;
  }
  if (value instanceof Set) {
    const valueAsArray = Array.from(value);
    return querystringSingleKey(key, valueAsArray, keyPrefix);
  }
  if (value instanceof Date) {
    return `${encodeURIComponent(fullKey)}=${encodeURIComponent(value.toISOString())}`;
  }
  if (value instanceof Object) {
    return querystring(value as HTTPQuery, fullKey);
  }
  return `${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`;
}

export interface RequestOpts {
  path: string;
  method: HTTPMethod;
  headers: HTTPHeaders;
  query?: HTTPQuery;
  body?: HTTPBody;
}

export interface FetchParams {
  url: string;
  init: RequestInit;
}

export interface RequestContext {
  url: string;
  init: RequestInit;
}

export interface ResponseContext {
  url: string;
  init: RequestInit;
  response: Response;
}

export interface ErrorContext {
  url: string;
  init: RequestInit;
  error: unknown;
  response?: Response;
}

export interface Middleware {
  pre?(context: RequestContext): Promise<FetchParams | void>;
  post?(context: ResponseContext): Promise<Response | void>;
  onError?(context: ErrorContext): Promise<Response | void>;
}

export interface ResponseTransformer<T> {
  (json: any): T;
}

export class JSONApiResponse<T> {
  constructor(public raw: Response, private transformer: ResponseTransformer<T> = (jsonValue: any) => jsonValue) {}

  async value(): Promise<T> {
    return this.transformer(await this.raw.json());
  }
}

export class BaseApi {
  constructor(private client: BaseApiClient) {}

  async request(
    path: string,
    method: HTTPMethod,
    pathParameters: Record<string, string>,
    query?: HTTPQuery,
    body?: HTTPBody,
  ): Promise<Response> {
    path = Object.entries(pathParameters).reduce(
      (acc, [paramName, paramValue]) => acc.replace(`{${paramName}}`, String(paramValue)),
      path,
    );

    return this.client.request({
      path,
      method,
      query,
      body,
      headers: {},
    });
  }
}

function isBlob(value: any): value is Blob {
  return typeof Blob !== 'undefined' && value instanceof Blob;
}

function isFormData(value: any): value is FormData {
  return typeof FormData !== 'undefined' && value instanceof FormData;
}

function removeEmpty(obj) {
  if (typeof obj === 'object') {
    Object.keys(obj).forEach((key) => (obj[key] === undefined ? delete obj[key] : {}));
  }
  return obj;
}

export class FetchError extends Error {
  override name: 'FetchError' = 'FetchError';
  constructor(public cause: Error, msg?: string) {
    super(msg);
  }
}

export class ResponseError extends Error {
  override name: 'ResponseError' = 'ResponseError';
  constructor(public response: Response, msg?: string) {
    super(msg);
  }
}

export type BaseApiClientOptions = {
  baseUrl: string;
  middlewares: Middleware[];
  getHeaders: () => HTTPHeaders;
};

export abstract class BaseApiClient {
  constructor(private options: BaseApiClientOptions) {}

  private async createFetchParams(context: RequestOpts): Promise<FetchParams> {
    let url = this.options.baseUrl + context.path;

    if (context.query !== undefined && Object.keys(context.query).length > 0) {
      url += `?${querystring(removeEmpty(context.query))}`;
    }

    const headers = { ...this.options.getHeaders(), ...context.headers };
    Object.keys(headers).forEach((key) => (headers[key] === undefined ? delete headers[key] : {}));

    const initParams = {
      method: context.method,
      headers,
      body: context.body,
    };

    const init: RequestInit = {
      ...initParams,
      body:
        isFormData(context.body) || context.body instanceof URLSearchParams || isBlob(context.body)
          ? context.body
          : JSON.stringify(removeEmpty(context.body)),
    };

    return { url, init };
  }

  async request(context: RequestOpts): Promise<Response> {
    let fetchParams = await this.createFetchParams(context);

    for (let i = 0; i < this.options.middlewares.length; i += 1) {
      const middleware = this.options.middlewares[i];
      if (middleware.pre) {
        fetchParams = (await middleware.pre(fetchParams)) || fetchParams;
      }
    }

    let response;

    try {
      response = await fetch(fetchParams.url, fetchParams.init);
    } catch (error) {
      for (let i = 0; i < this.options.middlewares.length; i += 1) {
        const middleware = this.options.middlewares[i];
        if (middleware.onError) {
          response =
            (await middleware.onError({
              error,
              url: fetchParams.url,
              init: fetchParams.init,
              response: response ? response.clone() : undefined,
            })) || response;
        }
      }

      if (response === undefined) {
        if (error instanceof Error) {
          throw new FetchError(error, 'The request failed and the interceptors did not return an alternative response');
        } else {
          throw error;
        }
      }
    }

    for (let i = 0; i < this.options.middlewares.length; i += 1) {
      const middleware = this.options.middlewares[i];
      if (middleware.post) {
        response =
          (await middleware.post({
            url: fetchParams.url,
            init: fetchParams.init,
            response: response.clone(),
          })) || response;
      }
    }

    if (response.status >= 200 && response.status < 300) {
      return response;
    }

    throw new ResponseError(response, 'Response returned an error code');
  }
}
